import { KiloVolts } from "@/libs/units/voltage";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator,
  type ValidationRule
} from "../scenarioValidator";

import {
  MAX_VOLT_LOST,
  MIN_CLEARANCE,
  SAGD_MLE_FAMILY
} from "../validatorConstants";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

import type { WellProperties } from "@/composables/forms/formTypes";
import type { Maybe } from "gql/api";
import type { Ref } from "vue";

import {
  calculateEllipseMaxDepth,
  getTubingCableODIn
} from "@/libs/calculations/cables";

export const getTubingAtDepth = (
  depth: number,
  wellProperties: Ref<Maybe<WellProperties>>
): { OD: number; couplingOD: number } => {
  const topTubing = wellProperties.value?.r_top_tubing;
  const bottomTubing = wellProperties.value?.r_bottom_tubing;
  const topTubingLength = wellProperties.value?.top_tubing_length_feet || 0;
  const bottomTubingLength =
    wellProperties.value?.bottom_tubing_length_feet || 0;

  if (depth <= topTubingLength) {
    return {
      OD: topTubing?.outer_diameter_inches || 0,
      couplingOD: topTubing?.coupling_outer_diameter_inches || 0
    };
  } else if (depth <= topTubingLength + bottomTubingLength) {
    return {
      OD: bottomTubing?.outer_diameter_inches || 0,
      couplingOD: bottomTubing?.coupling_outer_diameter_inches || 0
    };
  } else {
    return {
      OD: 0,
      couplingOD: 0
    };
  }
};

export const getCasingAtDepth = (
  depth: number,
  wellProperties: Ref<Maybe<WellProperties>>
): { setOD: number; traverseOD: number } => {
  const topCasing = wellProperties.value?.r_top_casing;
  const bottomCasing = wellProperties.value?.r_bottom_casing;
  const topCasingLength = wellProperties.value?.top_casing_length_feet || 0;
  const bottomCasingLength =
    wellProperties.value?.bottom_casing_length_feet || 0;

  if (depth <= topCasingLength) {
    return {
      setOD: topCasing?.drift_inches || 0,
      traverseOD: topCasing?.drift_inches || 0
    };
  } else if (depth <= topCasingLength + bottomCasingLength) {
    // setOD set at the bottom, but traverse because we had to go through the top
    return {
      setOD: bottomCasing?.drift_inches || 0,
      traverseOD: topCasing?.drift_inches || 0
    };
  } else {
    return {
      setOD: 0,
      traverseOD: 0
    };
  }
};

/** Sample both sides of each casing/tubing transition, not just cable endpoints.
 * A segment can pass its endpoints and still cross a narrower middle section. */
export function cablePathClearance(
  start: number, end: number, width: number, depth: number,
  wellProperties: Ref<Maybe<WellProperties>>
): number {
  const well = wellProperties.value;
  const points = [start, end];
  for (const boundary of [well?.top_casing_length_feet, well?.top_tubing_length_feet]) {
    if (typeof boundary === "number" && boundary > start && boundary < end) {
      points.push(boundary, Math.min(end, boundary + 1e-6));
    }
  }
  return Math.min(...points.map(point => {
    const casing = getCasingAtDepth(point, wellProperties);
    const tubing = getTubingAtDepth(point, wellProperties);
    return Math.min(casing.setOD, casing.traverseOD) - getTubingCableODIn(tubing.couplingOD, width, depth);
  }));
}

export const powerCableChecksNames = [
  "min_secondary_voltage",
  "max_secondary_voltage",
  "volt_loss",
  "power_cable_kv_rating",
  "power_cable_operating_temp_warning",
  "power_cable_operating_temp",
  "power_cable_length",
  "cable_non_lead",
  "motor_lead_cable_length_warning",
  "motor_lead_cable_length_error",
  "mle_temperature_warning",
  "mle_temperature",
  "mle_non_lead",
  "motor_max_amps_cable",
  "motor_max_amps_mle",
  "mle_max_pothead_temperature_check",
  "mle_clearance_traverse",
  "mle_clearance_set",
  "cable_on_surface_warning",
  "cable_on_surface_error",
  `cable_clearance`
] as const;

export const powerCableChecks: ValidationRuleGenerator = ({
  wellProperties,
  calcs,
  equipment,
  stack
}) => {
  function cableHandlesGas(family: string, isMLE = false): boolean {
    if (isMLE) {
      // If MLE, SAGD type can handle gas. Else check first party
      // of family name for 'L' (lead)
      if (family === SAGD_MLE_FAMILY) {
        return true;
      } else {
        const familyType = family.split(" ")[0];
        return familyType.includes("L");
      }
    } else {
      // If normal cable, check family name for 'L' (lead)
      return family.includes("L");
    }
  }

  const rules: ValidationRule[] = [];

  // MLE clearance check
  const mleEllipseMaxDepth = calculateEllipseMaxDepth(
    equipment.value?.r_mle?.width_inches || 0,
    equipment.value?.r_mle?.depth_inches || 0
  );

  // Iterate through all the pumps/seals/intakes and look for the largest outer_diameter_inches (LOOK FOR r_pump, r_seal, etc)
  const calcMleEquipmentOD = (): number => {
    const pumpDiameters = calcs.descendingOrderPumps.value.map(
      (xref): number => xref.r_pump?.housing_outer_diameter_inches || 0
    );
    const sealDiameters = calcs.descendingOrderSeals.value.map(
      (xref): number => xref.r_seal?.outer_diameter_inches || 0
    );
    const intakeDiameters = calcs.orderedIntakes.value.map(
      (xref): number => xref.r_intake?.outer_diameter_inches || 0
    );

    const allDiameters = [
      ...pumpDiameters,
      ...sealDiameters,
      ...intakeDiameters
    ];
    const maxDiameter = allDiameters.reduce(
      (max, diameter) => Math.max(max, diameter),
      0
    );

    return maxDiameter + mleEllipseMaxDepth;
  };

  const mleEquipmentOD = calcMleEquipmentOD();

  // const mleTopMD =
  //   calcs.motorLeadExtensionCalculations.mleCableData.value.mleStartMD;
  const mleBottomMD = calcs.motorLeadExtensionCalculations.mleCableData.value
    .mleEndMD as number;
  const mleCasing = getCasingAtDepth(mleBottomMD, wellProperties);
  const mleClearanceTraverse = mleCasing.traverseOD - mleEquipmentOD;
  const mleClearanceSet = mleCasing.setOD - mleEquipmentOD;

  rules.push({
    name: "mle_clearance_traverse",
    message:
      mleClearanceTraverse <= 0
        ? "Clearance Alert: MLE Cable with Equipment is too large for Casing during Install"
        : "MLE Clearance less than 0.1 inch during Install. Please review",
    severity: mleClearanceTraverse < 0 ? Severity.error : Severity.warn,
    isActive: valuesExist([
      equipment.value?.mle_id,
      wellProperties.value?.top_casing_id
    ]),
    isInvalid: mleClearanceTraverse < MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.CABLE
  });

  rules.push({
    name: "mle_clearance_set",
    message:
      mleClearanceSet < 0
        ? "Clearance Alert: MLE Cable with Equipment is too large for Casing at Set Depth"
        : "MLE Clearance less than 0.1 inch at Set Depth. Please review",
    severity: mleClearanceSet < 0 ? Severity.error : Severity.warn,
    isActive: valuesExist([
      equipment.value?.mle_id,
      wellProperties.value?.top_casing_id
    ]),
    isInvalid: mleClearanceSet < MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.CABLE
  });

  // Cable clearance — one alert if any cable (top or bottom) is too large for casing
  const cableClearanceResults: { minClearance: number; xrefId: number }[] = [];
  Object.values(calcs.cableCalculations.individual.value).forEach(cable => {
    if (cable.isSurface) {
      return;
    }
    const cableWidthIn = cable.xrefCable.r_cable?.width_inches ?? 0;
    const cableDepthIn = cable.xrefCable.r_cable?.depth_inches ?? 0;

    const minClearance = cablePathClearance(cable.downholeStartCableLengthFt,
      cable.downholeEndCableLengthFt, cableWidthIn, cableDepthIn, wellProperties);
    cableClearanceResults.push({
      minClearance,
      xrefId: cable.xrefCable.id
    });
  });

  const anyCableTooLarge = cableClearanceResults.some(r => r.minClearance < 0);
  const anyCableClearanceIssue = cableClearanceResults.some(
    r => r.minClearance < MIN_CLEARANCE
  );
  const affectedCableXrefIds = cableClearanceResults
    .filter(r => r.minClearance < MIN_CLEARANCE)
    .map(r => String(r.xrefId));

  rules.push({
    name: "cable_clearance",
    message: anyCableTooLarge
      ? "Clearance Error: Tubing + cable too large for casing"
      : "Clearance less than 0.1 inch during install. Please review",
    severity: anyCableTooLarge ? Severity.error : Severity.warn,
    isActive:
      valuesExist([
        wellProperties.value?.top_casing_length_feet,
        wellProperties.value?.top_tubing_length_feet
      ]) && calcs.orderedCables.value.length > 0,
    isInvalid: anyCableClearanceIssue,
    affectedXrefIds: affectedCableXrefIds,
    rulePlacement: EquipmentCalculatorSectionKey.CABLE
  });

  return [
    ...rules,

    // Minimum Secondary Voltage Check
    {
      name: "min_secondary_voltage",
      message: "Required Volts less than Min Secondary Voltage",
      severity: Severity.error,
      isActive: Boolean(equipment.value?.r_step_up_transformer),
      isInvalid:
        calcs.surfaceDriveCalculations.requiredVoltageV.value <
        (equipment.value?.r_step_up_transformer?.min_secondary_voltage ?? 0),
      rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
    },

    // Maximum Secondary Voltage Check
    {
      name: "max_secondary_voltage",
      message: "Required Volts greater than Max Secondary Voltage",
      severity: Severity.error,
      isActive: Boolean(equipment.value?.r_step_up_transformer),
      isInvalid:
        calcs.surfaceDriveCalculations.requiredVoltageV.value >
        (equipment.value?.r_step_up_transformer?.max_secondary_voltage ?? 0),
      rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
    },

    // Voltage loss check
    {
      name: "volt_loss",
      message: addStackPrefixToMessage(
        `Power Cable voltage loss exceeds ${MAX_VOLT_LOST}V per 1000 ft`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.orderedCables.value.some(
        xref =>
          calcs.cableCalculations.individual.value[xref.id]
            .voltLossPerThousandFTV > MAX_VOLT_LOST
      ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(
          xref =>
            calcs.cableCalculations.individual.value[xref.id]
              .voltLossPerThousandFTV > MAX_VOLT_LOST
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },

    // Voltage rating check
    {
      name: "power_cable_kv_rating",
      message: addStackPrefixToMessage(
        `Power Cable voltage rating less than surface voltage`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([calcs.surfaceDriveCalculations.requiredVoltageV]),
      isInvalid: calcs.orderedCables.value.some(
        xref =>
          KiloVolts.toStandardUnit(xref.r_cable?.kv || 0) <
          calcs.surfaceDriveCalculations.requiredVoltageV.value
      ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(
          xref =>
            KiloVolts.toStandardUnit(xref.r_cable?.kv || 0) <
            calcs.surfaceDriveCalculations.requiredVoltageV.value
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // Temp check warning
    {
      name: "power_cable_operating_temp_warning",
      message: addStackPrefixToMessage(
        `Power Cable temperature above 90% rated temperature`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.orderedCables.value.some(
        xref =>
          calcs.cableCalculations.individual.value[xref.id]
            .cableMaxTemperatureF /
            (xref.r_cable?.max_temperature_fahrenheit || 0) >=
          0.9
      ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(
          xref =>
            calcs.cableCalculations.individual.value[xref.id]
              .cableMaxTemperatureF /
              (xref.r_cable?.max_temperature_fahrenheit || 0) >=
            0.9
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // Temp check
    {
      name: "power_cable_operating_temp",
      message: addStackPrefixToMessage(
        `Power Cable temperature above rated temperature`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.orderedCables.value.some(
        xref =>
          calcs.cableCalculations.individual.value[xref.id]
            .cableMaxTemperatureF /
            (xref.r_cable?.max_temperature_fahrenheit || 1) >
          1
      ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(
          xref =>
            calcs.cableCalculations.individual.value[xref.id]
              .cableMaxTemperatureF /
              (xref.r_cable?.max_temperature_fahrenheit || 1) >
            1
        )
        .map(xref => xref.id),
      disablesRules: ["power_cable_operating_temp_warning"],
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // Cable Gas Lead
    {
      name: "cable_non_lead",
      message: addStackPrefixToMessage(
        `Potential Cable Gas Impregnation - Lead Barrier Recommended`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio
      ]),
      isInvalid:
        calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio >
          0 &&
        calcs.orderedCables.value.some(
          xref => !cableHandlesGas(xref.r_cable?.family || "")
        ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(xref => !cableHandlesGas(xref.r_cable?.family || ""))
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // Surface cable length check
    {
      name: "cable_on_surface_warning",
      message: addStackPrefixToMessage(
        `Recommended to have 100ft (30m) in excess cable`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive:
        calcs.equipmentLengths.equipmentLengthFt.value > 0 &&
        calcs.orderedCables.value.length > 0,
      isInvalid:
        Number(calcs.equipmentLengths.cableSurfaceLengthFt.value.toFixed(2)) <
        100,
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    {
      name: "cable_on_surface_error",
      message: addStackPrefixToMessage(
        `Cable does not make it to surface. Please increase length`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        calcs.equipmentLengths.equipmentLengthFt.value > 0 &&
        calcs.orderedCables.value.length > 0,
      isInvalid:
        Number(calcs.equipmentLengths.totalCableLengthFt.value.toFixed(2)) <
        Number(calcs.equipmentLengths.depthToReachMLEPlugFt.value.toFixed(2)),
      disablesRules: ["cable_on_surface_warning"],
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // MLE Length
    {
      name: "motor_lead_cable_length_warning",
      message: addStackPrefixToMessage(
        `MLE recommended to be 10ft (3m) past Discharge`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: valuesExist([
        equipment.value?.r_mle?.length_feet,
        calcs.equipmentLengths.lengthForMleFt
      ]),
      isInvalid:
        calcs.equipmentLengths.lengthForMleFt.value + 10 >
        equipment.value?.r_mle?.length_feet,
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },
    {
      name: "motor_lead_cable_length_error",
      message: addStackPrefixToMessage(
        `MLE does not exceed Discharge`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        equipment.value?.r_mle?.length_feet,
        calcs.equipmentLengths.lengthForMleFt
      ]),
      isInvalid:
        calcs.equipmentLengths.lengthForMleFt.value >
        equipment.value?.r_mle?.length_feet,
      disablesRules: ["motor_lead_cable_length_warning"],
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },
    // MLE Temp Warning
    {
      name: "mle_temperature_warning",
      message: addStackPrefixToMessage(
        `MLE temperature above 90% rated temperature`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive:
        Boolean(equipment.value?.r_mle?.max_temperature_fahrenheit) &&
        valuesExist([
          calcs.motorLeadExtensionCalculations.mleCableData.value
            .cableMaxTemperatureF
        ]),
      isInvalid:
        calcs.motorLeadExtensionCalculations.mleCableData.value
          .cableMaxTemperatureF /
          (equipment.value?.r_mle?.max_temperature_fahrenheit || 1) >=
        0.9,
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },
    // MLE Temp
    {
      name: "mle_temperature",
      message: addStackPrefixToMessage(
        `MLE Temperature exceeds maximum cable temperature`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        Boolean(equipment.value?.r_mle?.max_temperature_fahrenheit) &&
        valuesExist([
          calcs.motorLeadExtensionCalculations.mleCableData.value
            .cableMaxTemperatureF
        ]),
      isInvalid:
        calcs.motorLeadExtensionCalculations.mleCableData.value
          .cableMaxTemperatureF /
          (equipment.value?.r_mle?.max_temperature_fahrenheit || 1) >
        1,
      disablesRules: ["mle_temperature_warning"],
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },
    // MLE Gas Lead
    {
      name: "mle_non_lead",
      message: addStackPrefixToMessage(
        `Potential MLE Gas Impregnation - Lead Barrier Recommended`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        Boolean(equipment.value?.r_mle?.family) &&
        valuesExist([
          calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio
        ]),
      isInvalid:
        calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio >
          0 && !cableHandlesGas(equipment.value?.r_mle?.family || "", true),
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },
    // Cable Max Amps
    {
      name: "motor_max_amps_cable",
      message: addStackPrefixToMessage(
        "Ampacity exceeded for selected cable at operating conditions",
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([calcs.motorCalculations.motorCurrentA]) &&
        calcs.orderedCables.value.length > 0,
      isInvalid: calcs.orderedCables.value.some(
        xref =>
          calcs.cableCalculations.individual.value[xref.id]
            ?.maxAmpsAtMaxTemperatureA <
          calcs.motorCalculations?.motorCurrentA?.value
      ),
      affectedXrefIds: calcs.orderedCables.value
        .filter(
          xref =>
            calcs.cableCalculations.individual.value[xref.id]
              ?.maxAmpsAtMaxTemperatureA <
            calcs.motorCalculations?.motorCurrentA?.value
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.CABLE
    },
    // MLE Max Amps
    {
      name: "motor_max_amps_mle",
      message: "Ampacity exceeded for selected MLE at operating conditions",
      severity: Severity.error,
      isActive:
        valuesExist([calcs.motorCalculations.motorCurrentA]) &&
        Boolean(equipment.value?.r_mle?.id),
      isInvalid:
        calcs.motorLeadExtensionCalculations.mleCableData.value
          .maxAmpsAtMaxTemperatureA <
        calcs.motorCalculations.motorCurrentA.value,
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    },

    // MLE Max Pothead exceeded
    {
      name: "mle_max_pothead_temperature_check",
      message:
        "Maximum recommended pothead temp exceeded. Please select a different MLE.",
      severity: Severity.error,
      isActive: Boolean(
        equipment.value?.r_mle?.id &&
          calcs.motorCalculations.motorInternalTemperatureF.value
      ),
      isInvalid:
        calcs.motorCalculations.motorInternalTemperatureF.value >
        (equipment.value?.r_mle?.max_pothead_temperature_fahrenheit ?? 0),
      rulePlacement: EquipmentCalculatorSectionKey.MLE
    }
  ];
};
